/**
 * GeoGebra指令生成相关API
 * 包含指令生成和处理功能
 */

import Logger from "../utils/logger";
import { API_PATHS } from "./config";
import { convertImageToBase64, requestManager, getCurrentUser, getCustomClientSlug, requestModelByModeConfig } from "./utils";

const GGB_COMMAND_NAMES = [
  "Angle",
  "Arc",
  "Area",
  "Center",
  "CenterView",
  "Circle",
  "Circumcenter",
  "Delete",
  "Derivative",
  "Distance",
  "Ellipse",
  "Extremum",
  "Focus",
  "Function",
  "Hyperbola",
  "If",
  "Integral",
  "Intersect",
  "Length",
  "Line",
  "Midpoint",
  "Parabola",
  "Perimeter",
  "Point",
  "Polygon",
  "Radius",
  "Ray",
  "Rename",
  "Root",
  "Sector",
  "Segment",
  "Sequence",
  "SetCaption",
  "SetColor",
  "SetFilling",
  "SetFixed",
  "SetLabel",
  "SetLabelMode",
  "SetLayer",
  "SetLineStyle",
  "SetLineThickness",
  "SetPointSize",
  "SetTrace",
  "SetValue",
  "SetVisible",
  "ShowAxes",
  "ShowGrid",
  "ShowLabel",
  "Solutions",
  "Tangent",
  "Text",
  "UpdateConstruction",
  "Vector",
  "Vertex",
  "向量",
  "线段",
  "直线",
  "圆",
  "点",
  "多边形",
  "角度",
  "中点",
  "交点",
  "射线",
  "垂直线",
  "平行线",
  "切线",
  "圆弧",
  "扇形",
  "椭圆",
  "双曲线",
  "抛物线",
  "函数",
  "积分",
  "导数",
  "极值",
  "根",
  "解",
  "序列",
  "列表",
  "文本",
  "如果",
  "长度",
  "距离",
  "面积",
  "周长",
  "半径",
  "圆心",
  "焦点",
  "顶点",
  "斜率",
  "显示标签",
  "设置颜色",
  "设置线型",
  "设置线宽",
  "设置点大小",
  "设置标题",
  "设置标签模式",
  "设置可见性",
  "设置图层",
  "设置固定",
  "设置轨迹",
  "设置填充",
  "重命名",
  "删除",
  "更新",
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const GGB_OBJECT_NAME = "[A-Za-z][A-Za-z0-9_]*(?:_\\{[^}]+\\})?";

const GGB_KNOWN_COMMAND_PATTERN = new RegExp(
  `^(?:${GGB_COMMAND_NAMES.map(escapeRegExp).join("|")})\\s*(?:\\(|\\[)`,
  "i"
);
const GGB_GENERIC_COMMAND_PATTERN = /^[A-Z][A-Za-z0-9_]*\s*(?:\([\s\S]*\)|\[[\s\S]*\])\s*;?$/;
const GGB_ASSIGNMENT_PATTERN = new RegExp(`^${GGB_OBJECT_NAME}(?:\\s*\\([^)]*\\))?\\s*(?:=|:)`);
const GGB_BARE_EQUATION_PATTERN = /^[0-9A-Za-z+\-*/^().,\s]+=[0-9A-Za-z+\-*/^().,\s]+$/;

const isLikelyGeoGebraCommand = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("#") || trimmed.startsWith("//")) return false;

  // 过滤掉明显的解释性文字
  if (
    trimmed.startsWith("以下是") ||
    trimmed.startsWith("这些指令") ||
    trimmed.startsWith("根据") ||
    trimmed.includes("GeoGebra指令") ||
    trimmed.includes("说明") ||
    trimmed.includes("解释")
  ) {
    return false;
  }

  // 只保留明确的 GeoGebra 指令形态，避免把含有括号的解释性句子误收进作品。
  return (
    GGB_ASSIGNMENT_PATTERN.test(trimmed) ||
    GGB_KNOWN_COMMAND_PATTERN.test(trimmed) ||
    GGB_GENERIC_COMMAND_PATTERN.test(trimmed) ||
    GGB_BARE_EQUATION_PATTERN.test(trimmed)
  );
};

const normalizeGeoGebraLines = (lines) =>
  lines
    .flatMap(splitPotentialGeoGebraLines)
    .filter((line) => line.length > 0)
    .filter(isLikelyGeoGebraCommand);

const cleanPotentialGeoGebraLine = (line) =>
  line
    .trim()
    .replace(/^```(?:geogebra|ggb)?\s*/i, "")
    .replace(/^```$/, "")
    .replace(/^\d+\s*[.)、:：]\s*/, "")
    .replace(/^[-*]\s+/, "")
    .trim();

const splitPotentialGeoGebraLines = (line) => {
  const cleaned = cleanPotentialGeoGebraLine(line);
  if (!cleaned) return [];

  // 修复模型偶发的换行丢失：如 "f = 1.2t = Slider(...)" 应拆成两条指令。
  return cleaned
    .replace(
      new RegExp(`([^\\s,;])\\s+(${GGB_OBJECT_NAME}\\s*(?:=|:))`, "g"),
      "$1\n$2"
    )
    .replace(
      new RegExp(`(\\d(?:\\.\\d+)?)(?=(${GGB_OBJECT_NAME}\\s*(?:=|:)))`, "g"),
      "$1\n"
    )
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const stripThinkBlocks = (text) =>
  text
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/<think>[\s\S]*$/i, "");

/**
 * 从AI响应中提取GeoGebra指令
 * @param {string} response AI的完整响应
 * @returns {string} 提取的GeoGebra指令
 */
const extractGeoGebraCommands = (response) => {
  try {
    Logger.debug("开始提取GeoGebra指令", { responseLength: response.length });

    // 移除可能的解释性文字，只保留指令
    let commands = stripThinkBlocks(response);

    // 统一使用 normalizeGeoGebraLines，保持与流式提取逻辑一致
    const commandLines = normalizeGeoGebraLines(commands.split("\n"));
    commands = commandLines.join("\n");

    Logger.debug("GeoGebra指令提取完成", {
      finalLength: commands.length,
      commandCount: commands.split("\n").filter((cmd) => cmd.trim()).length,
    });

    return commands;
  } catch (error) {
    Logger.error("提取GeoGebra指令失败", error);
    return response; // 失败时返回原始响应
  }
};

/**
 * 生成GeoGebra绘图指令
 * @param {string} prompt 用户输入的问题描述
 * @param {Function} onChunkReceived 接收流式响应的回调函数
 * @param {Function} onComplete 完成时的回调函数
 * @param {Function} onError 错误处理回调函数
 * @param {File|File[]} imageFiles 可选的图片文件（最多3张）
 * @param {boolean} freeMode 是否使用免费模型与免费渠道
 */
export const streamGeoGebraCode = async (
  prompt,
  onChunkReceived,
  onComplete,
  onError,
  imageFiles,
  freeMode = false
) => {
  const imageFilesArr = Array.isArray(imageFiles) ? imageFiles : (imageFiles ? [imageFiles] : []);
  Logger.info("开始生成GeoGebra指令", { prompt, imageCount: imageFilesArr.length });

  try {
    await requestManager.enqueue(async () => {
      try {
        let lineBuffer = "";
        let isInsideThinkBlock = false;
        let emittedAnyCommands = false;

        const stripThinkSegmentsFromLine = (line) => {
          let remaining = line;
          let output = "";

          while (remaining) {
            if (isInsideThinkBlock) {
              const endIndex = remaining.search(/<\/think>/i);
              if (endIndex === -1) {
                return output;
              }
              remaining = remaining.slice(endIndex).replace(/^<\/think>/i, "");
              isInsideThinkBlock = false;
              continue;
            }

            const startIndex = remaining.search(/<think>/i);
            if (startIndex === -1) {
              output += remaining;
              break;
            }

            output += remaining.slice(0, startIndex);
            remaining = remaining.slice(startIndex).replace(/^<think>/i, "");
            isInsideThinkBlock = true;
          }

          return output;
        };

        const emitCommandLines = (chunk, flush = false) => {
          lineBuffer += chunk || "";
          const lines = lineBuffer.split(/\r?\n/);
          lineBuffer = flush ? "" : lines.pop() || "";
          const stableLines = flush ? lines : lines;
          const commandsToEmit = [];

          for (const rawLine of stableLines) {
            const visibleLine = stripThinkSegmentsFromLine(rawLine);
            const command = cleanPotentialGeoGebraLine(visibleLine);
            if (!command || !isLikelyGeoGebraCommand(command)) {
              continue;
            }
            commandsToEmit.push(command);
          }

          if (flush && lineBuffer) {
            const visibleLine = stripThinkSegmentsFromLine(lineBuffer);
            const command = cleanPotentialGeoGebraLine(visibleLine);
            if (command && isLikelyGeoGebraCommand(command)) {
              commandsToEmit.push(command);
            }
            lineBuffer = "";
          }

          if (commandsToEmit.length === 0) return;

          emittedAnyCommands = true;
          if (onChunkReceived) {
            onChunkReceived(commandsToEmit.join("\n"));
          }
        };

        // 构建请求体 - 系统提示词和模型参数已移至后端
        const requestBody = {
          mode: "geogebra", // GeoGebra指令生成模式
          prompt: prompt, // 用户的请求内容
          stream: true, // 开启流式响应
          free_mode: freeMode,
        };
        const slug = getCustomClientSlug();
        if (slug) requestBody.custom_client_slug = slug;

        // 如果有图片，将所有图片转为 base64 数组
        if (imageFilesArr.length > 0) {
          const base64Images = await Promise.all(imageFilesArr.map(convertImageToBase64));
          requestBody.images = base64Images;
        }

        Logger.debug("发送GeoGebra指令生成非流式API请求", requestBody);

        // 添加用户ID
        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;

        const { fullResponse, chargeSession } = await requestModelByModeConfig(
          requestBody,
          (chunk) => emitCommandLines(chunk)
        );

        Logger.info("GeoGebra指令生成完成", {
          responseLength: fullResponse.length,
        });

        const commands = extractGeoGebraCommands(fullResponse);
        emitCommandLines("", true);
        if (onChunkReceived && commands && !emittedAnyCommands) {
          onChunkReceived(commands);
        }

        if (!commands || commands.trim() === "") {
          Logger.error("提取的GeoGebra指令为空");
          onError("生成的GeoGebra指令为空，请重试");
          return;
        }

        Logger.info("GeoGebra指令提取成功", {
          commandsLength: commands.length,
          commandCount: commands.split("\n").filter((cmd) => cmd.trim()).length,
        });

        onComplete(commands, chargeSession);
      } catch (error) {
        Logger.error("GeoGebra指令生成失败", error);
        const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : `生成GeoGebra指令失败: ${error?.message || error}`;
        onError(msg);
      }
    });
  } catch (error) {
    Logger.error("GeoGebra请求队列处理失败", error);
    const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : `请求队列处理失败: ${error?.message || error}`;
    onError(msg);
  }
};

export default {
  streamGeoGebraCode,
};
